<template>
    <div>
        <div id="form-export">
          <div :class="['pt-3 pb-4', 'form-export-' + (darkMode ? 'dark' : 'bright')]">
            <h1 v-if="showHeading" class="white">Export</h1>
            <div class="lighter mb-1">{{ $t('Please select a format for your export.') }}</div>
            <!-- Word -->
            <div
              :class="['settings-button float-left mr-1 d-inline-block with-distinct-border',
              showExportPrompt === exportPossibilites.WORD ? 'orange no-hover' : 'switched-colors']"
              @click="
                showExportPrompt = exportPossibilites.WORD;
                autoGenerateLegend = false;
              "
            >
              <icon class="mr-2" type="file-word" />
              <loading-spinner  v-if="wordGenerating" class="white ml-2 float-right" />
              {{ $t("exportPrompt.word") }}
              <div class="d-inline-block ml-1" v-if="wordGenerating">
                ({{ currentWordPage }} / {{ totalWordPages }})
              </div>
            </div>
  
            <!-- PDF -->
            <div
                :class="['settings-button mr-1 d-inline-block with-distinct-border',
                showExportPrompt === exportPossibilites.PDF ? 'orange no-hover' : 'switched-colors']"
              @click="
                showExportPrompt = exportPossibilites.PDF;
                showSessionSelection = false;
              "
            >
              <icon
                v-if="!pdfGenerating"
                class="mr-2"
                type="pdf"
              />
              <loading-spinner v-if="pdfGenerating" class="white ml-2 float-right" />
              {{ $t("exportPrompt.pdf") }}
              <div class="d-inline-block ml-1" v-if="pdfGenerating">
                ({{ currentPDFPage }} / {{ totalPDFPages }})
              </div>
            </div>

            <!-- Language Export -->
            <div
                :class="['settings-button mr-1 d-inline-block with-distinct-border',
                showExportPrompt === exportPossibilites.LANGUAGE ? 'orange no-hover' : 'switched-colors']"
                @click="
                  showExportPrompt = exportPossibilites.LANGUAGE;
                "
            >
              <icon class="mr-2" type="globe" />
              {{ $t("vform.language") }}
              </div>
          </div>
          <div class="w-100" />
          <div v-if="finishedMessage" class="form-error p-1 w-100">
            {{ finishedMessage}}
          </div>

          <div :class="['col-12 border-radius form-export-details', darkMode ? 'darker' : 'brighter',!showExportPrompt ? 'collapsed' : '']">
            <!-- Word Prompt popup -->
           <word-export
              v-if="showExportPrompt === exportPossibilites.WORD"
              :meta-sets="metaSets"
              :project-id="projectId"
              :organization-id="organizationId"
              :slides="slides"
              :config="config"
              :project-name="projectName"
              :project-loading-mode="projectLoadingMode"
           />

            <!-- PDF Prompt -->
            <p-d-f-export
              v-if="showExportPrompt === exportPossibilites.PDF"
              :log-id="logId"
              :config="config"
              :organization-id="organizationId"
              :project-id="projectId"
              :form-id="formId"
              :slides="slides"
              :project-loading-mode="projectLoadingMode"
            />

            <language-export
                v-if="showExportPrompt === exportPossibilites.LANGUAGE"
                :config="config"
              />
          </div>
        </div>
    </div>
  </template>
  
  <script>
  import Icon from "@/components/Icon.vue";
  import LoadingSpinner from "@/components/LoadingSpinner.vue";
  import toPDFMixinJs from "@/components/vForm/mixins/toPDFMixin.js.vue";
  import { mapState } from "vuex";
  import {projectLoadingModes, vformLoadingModes} from "@/enum";
  import vFormAndProjectMixin from "@/components/vForm/mixins/vFormAndProjectMixin.js.vue";
  import WordExport from "@/components/vFormImportExport/WordExport.vue";
  import PDFExport from "@/components/vFormImportExport/PDFExport.vue";
  import LanguageExport from "@/components/vFormImportExport/LanguageExport.vue";
  export default {
    name: "VFormExport",
    components: {
      WordExport,
      Icon,
      LoadingSpinner,
      PDFExport,
      LanguageExport
    },
    mixins: [toPDFMixinJs, vFormAndProjectMixin],
    props: {
      logId: { type: String, default: "" },
      organizationId: {type: String, required: true},
      projectId: {type: String, required: true},
      formId: {type: String, required: true},
      projectName: {type: String, required: true},
      showCloseButton: {type: Boolean, default: true},
      showHeading: {type: Boolean, default: true},
      darkMode: {type: Boolean, default: true},
      projectLoadingMode: {type: String, default: projectLoadingModes.ONLINE},
      offlineMode: {type: Boolean, default: false},
    },
    data() {
      return {
        finishedMessage: "",
        formLoading: false,
        showExportSettings: false,
        showSessionSelection: false,
        showExportPrompt: "",
        exportPossibilites: Object.freeze({
          WORD: 'Word',
          PDF: 'PDF',
          LANGUAGE: 'Language',
        }),
  
        // Word Export settings
        selectedMetaSet: "",
        metaFields: [],
        selectedMetaField: "",
        template: ["Tables"],
        autoGenerateLegend: false,
        generatePartList: false,

        // PDF Export Settings
        metaSets: [],

        config: {},
        slides: [],
      };
    },
    computed: {
      ...mapState({
        totalPDFPages: (state) => state.formpage.totalPDFPages,
        currentPDFPage: (state) => state.formpage.currentPDFPage,
        pdfGenerating: (state) => state.formpage.isPDFGenerating,
        pdfReportGenerating: (state) => state.formpage.pdfReportGenerating,
        currentWordPage: (state) => state.formpage.currentWordPage,
        totalWordPages: (state) => state.formpage.totalWordPages,
        wordGenerating: (state) => state.formpage.isWORDGenerating,
      }),
    },
    beforeMount() {
      this.loadMetaSets();
      this.loadFormAndProjectConfig();
    },
    methods: {
      async loadFormAndProjectConfig() {
        this.formLoading = true;
        console.log('loading vForm')
        if(!this.projectLoadingMode) {
          this.projectLoadingMode = this.offlineMode ? projectLoadingModes.OFFLINE : await this.getProjectLoadingMode(this.projectId);
        }
        console.log('offlineMode', this.offlineMode, 'projectLoadingMode', this.projectLoadingMode)
        const {content} = await this.loadFormNew({
          loadingMode: this.offlineMode ? vformLoadingModes.OFFLINE : vformLoadingModes.CONTENT, // todo: when vform moves to project storage, change here to dynamic
          formId: this.formId,
          projectId: this.projectId
        })
        this.config = content;
        console.log('loaded vForm', this.config)
        this.projectConfig = await this.loadProjectConfigFileNew(this.projectId, {loadingMode: this.projectLoadingMode})
        this.slides = this.projectConfig && this.projectConfig.slides ? this.projectConfig.slides : [{id: "none"}];
        this.formLoading = false;
      },
      async loadMetaSets() {
        await this.$store.dispatch('clientLoadMetaSets', {
          include: 'metaFields',
          filter: 'organizationId eq ' + this.organizationId
        }).then(metaSets => {
          this.metaSets = metaSets;
        });
      },
    }
  };
  </script>




<style lang="scss" scoped>
.form-export-details {
  transition: all 300ms ease;
  max-height: 1000px;
  padding: 20px;
  background-color: rgba(0,0,0,0.5);
  min-height: 230px;
  &.collapsed {
    height: 0;
    padding: 0;
    overflow: hidden;
    min-height: 0;
  }
}
</style>

<style lang="scss">
$left-side: 160px;
.newStyle #form-export {
  h1.white {
    color: rgba(255,255,255,0.8);
  }
  h2 {
    font-family: 'Inter bold', sans-serif;
    font-weight: bold;
    text-transform: none;
    color: rgba(255,255,255,0.7);
    font-size: 1.2rem;
  }
}
.left-side {
  width: $left-side;
}
.generate-legend-box {
  border-top: 1px solid rgba(255,255,255,0.4);
  padding-top: 15px;
  margin-top: 15px;
  // margin-left: $left-side - 10px;
}
.form-export-bright {
  background-color: #f5f5f5;
  padding: 15px;
  h1, h2 {
    color: #333 !important;
  }
}
.form-export-details {
  &.brighter {
    background-color: #f5f5f5;
    label, h2 {
      color: #333 !important;
    }
  }
}
</style>
